import { ApolloError, gql } from "@apollo/client";
import { ReactNode, createContext, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import * as OrgConfig from "src/generated/hooks/org-config/requests";
import * as RD from "src/types/remoteData";
import { DeepReadonly } from "src/types/utils";
import * as GQL from "../types/graphql";
import * as RemoteData from "../types/remoteData";
import { useOrgConfig } from "./useOrgConfig";
import { useRemoteDataQuery } from "./useRemoteDataQuery";

const DefaultConfig = OrgConfig.ContactsConfigSchema.default;

export function useContactMethods(): RD.RemoteData<
  unknown,
  readonly DeepReadonly<OrgConfig.ContactConfig>[]
> {
  return useOrgConfig<OrgConfig.ContactsConfig>("Contacts", DefaultConfig).map(
    (config) => {
      // If it's disabled config, return empty array
      if (config.disabled) {
        return [];
      }

      // Otherwise, return just the contacts array
      return config.contacts;
    }
  );
}

export const ORGANIZATION_FRAGMENT = gql`
  fragment Organization on organization {
    id
    name
    path
    timezone_name
  }
`;

export const GET_ORGANIZATION_ID = gql`
  ${ORGANIZATION_FRAGMENT}
  query GetOrganizationId($path: String!) {
    organization(where: { path: { _eq: $path } }, limit: 1) {
      ...Organization
    }
  }
`;

export type UseOrganizationType = RemoteData.RemoteData<
  OrganizationError,
  GQL.Organization
>;

export type OrganizationError = ServerError | NotFoundError;
export class ServerError {
  readonly kind = "ServerError";
  constructor(public error: ApolloError) {}
}
export class NotFoundError {
  readonly kind = "NotFoundError";
}

export function isOrganizationError(
  error: unknown
): error is OrganizationError {
  return error instanceof ServerError || error instanceof NotFoundError;
}

export function useOrganization(): UseOrganizationType {
  const context = useContext(OrganizationContext);
  return context.organization;
}

export function useTimezoneName(): string {
  const organization = useOrganization();
  const timezoneName: string = organization
    .map((o) => o.timezone_name)
    .withDefault("+00:00");
  return timezoneName;
}

// Load the contacts.json from Cloudfront
export const useOrganizationContacts = () => {
  const contactMethodsRemoteData = useContactMethods();

  // Return empty array while loading or if there's an error
  if (!contactMethodsRemoteData.hasData()) {
    return [];
  }

  // Get the contact methods from the RemoteData
  const contactMethods = contactMethodsRemoteData.data;
  return contactMethods;
};

export function hasOrganizations(
  organization: UseOrganizationType,
  ...orgPaths: string[]
) {
  if (organization.hasData()) {
    return orgPaths.some((path) => path === organization.data.path);
  }

  return false;
}

/**
 * Organization Context
 */
type OrganizationContextType = {
  organization: UseOrganizationType;
};

export const OrganizationContext = createContext<OrganizationContextType>({
  organization: RD.notAsked(),
});

// Create the provider component
type OrganizationProviderProps = {
  children: ReactNode;
};

export const OrganizationProvider = ({
  children,
}: OrganizationProviderProps) => {
  const { organization: orgPath } = useParams();
  const { remoteData } = useRemoteDataQuery<
    GQL.GetOrganizationId,
    GQL.GetOrganizationIdVariables
  >(GET_ORGANIZATION_ID, {
    variables: { path: orgPath ?? "" },
    skip: orgPath === undefined,
  });

  const organization: UseOrganizationType = useMemo(() => {
    return remoteData
      .mapError<OrganizationError>((error) => new ServerError(error))
      .andThen((data) => {
        const organizationData = data.organization[0];
        if (organizationData === undefined)
          return RemoteData.failure(new NotFoundError());

        return RemoteData.success(organizationData);
      });
  }, [remoteData]);

  return (
    <OrganizationContext.Provider value={{ organization }}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const MockedOrganizationProvider = ({
  organization,
  children,
}: {
  organization: GQL.Organization;
  children: ReactNode;
}) => {
  return (
    <OrganizationContext.Provider
      value={{ organization: RD.success(organization) }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};
